/**
 * Simulation context: scenario, simulated date, region and persona. Mocks and
 * components read the simulated "now" from here — never `new Date()` — so a
 * demo can be moved to any day and screenshots stay deterministic.
 */
import { getSimState, setSimState, useSimStore } from "./store";
import { IS_SHOTS } from "./params";
import type { SimContextState } from "./types";

export function useSimContext(): SimContextState {
  return useSimStore((s) => s.context);
}

export function getSimContext(): SimContextState {
  return getSimState().context;
}

export function setSimContext(patch: Partial<SimContextState>): void {
  setSimState((s) => ({ ...s, context: { ...s.context, ...patch } }));
}

/** The simulated date at the real time of day (09:30 sharp in screenshot mode). */
export function getSimNow(): Date {
  const [y = 2026, m = 1, d = 1] = getSimContext().simDate.split("-").map(Number);
  if (IS_SHOTS) return new Date(y, m - 1, d, 9, 30, 0);
  const real = new Date();
  return new Date(y, m - 1, d, real.getHours(), real.getMinutes(), real.getSeconds());
}

/** ISO timestamp the given amount of time before the simulated now — for seed data. */
export function simIsoAgo({ days = 0, hours = 0, minutes = 0 } = {}): string {
  const ms = ((days * 24 + hours) * 60 + minutes) * 60_000;
  return new Date(getSimNow().getTime() - ms).toISOString();
}

/** "just now", "12m ago", "3h ago", "2d ago", then a short date. */
export function timeAgo(iso: string): string {
  const diff = Math.max(0, getSimNow().getTime() - new Date(iso).getTime());
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}
